import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import useTranslate from "../../../modules/local/useTranslate";
import {FaRegTrashCan} from "react-icons/fa6";
import {Avatar, Button, Input} from "antd";
import classNames from "classnames";
import TextArea from "antd/lib/input/TextArea";
import {BsCheck2, BsClockHistory, BsDashLg, BsFileEarmarkText, BsPlusLg, BsXCircle} from "react-icons/bs";
import {LayoutContext} from "../../layouts/Default/LayoutContext";
import {PiMinusBold, PiPlusBold} from "react-icons/pi";
import {TbTrash} from "react-icons/tb";

// Thang trạng thái điểm danh với status là số nguyên và tên tiếng Anh
const ATTENDANCE_STATUSES = [
    {
        icon: <BsCheck2 size={16} className="text-green-600"/>,
        display: 'in attendance', description: 'on time description',
        status: 1, requiresNote: false,
        color: 'text-green-500', colorButton: 'bg-green-200', colorBg: 'bg-green-100', borderColor: 'border-green-400'
    }, // Green-600
    {
        icon: <BsClockHistory size={16} className="text-yellow-600"/>,
        display: 'late', description: 'late for class description',
        status: -3, requiresNote: true,
        color: 'text-yellow-500', colorButton: 'bg-yellow-200', colorBg: 'bg-yellow-100', borderColor: 'border-yellow-400'
    }, // Yellow-600
    {
        icon: <BsFileEarmarkText size={16} className="text-orange-600"/>,
        display: 'excused absence', description: 'excused absence description',
        status: -1, requiresNote: true,
        color: 'text-orange-500', colorButton: 'bg-orange-200', colorBg: 'bg-orange-100', borderColor: 'border-orange-400'
    }, // Blue-600
    {
        icon: <BsXCircle size={16} className="text-primary-600"/>,
        display: 'unexcused absence', description: 'unexcused absence description',
        status: -2, requiresNote: true,
        color: 'text-primary-500', colorButton: 'bg-primary-200', colorBg: 'bg-primary-100', borderColor: 'border-primary-400'
    }, // Red-600
];

/**
 * Component hiển thị trạng thái điểm danh và lý do/ghi chú.
 */
const AttendanceDisplay = ({status, absentReason, numberOfSessions, currentStatusConfig, onClear, onEdit}) => {
    const t = useTranslate();

    return (
        <div className="flex flex-col p-4 border border-slate-200 bg-slate-50 shadow-md rounded-2xl text-gray-800 relative transition-all duration-300">
            {status !== null && (
                <Button
                    color="primary" variant="filled"
                    onClick={onClear}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    className="bg-primary-50 hover:bg-primary-100 rounded-md text-primary-500 hover:text-primary no-border transition duration-200"
                    title={t('clear')}
                    icon={<TbTrash size={14}/>}
                />
            )}
            <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 tracking-widest uppercase flex-shrink-0">
                    {t('selected status')}
                </span>
            </div>

            <span className={`font-extrabold text-xl md:text-2xl ${currentStatusConfig?.color}`} >
                {currentStatusConfig ? t(currentStatusConfig.display) : `— ${t('attendance status')} —`}
            </span>

            <div className="mt-1 flex items-center gap-1 text-xs ">
                <span className="text-gray-600 tracking-wider">
                    {`${t('number sessions')}: `}
                </span>
                <span className="font-semibold text-gray-800 tracking-wide">
                    {numberOfSessions} {t('sessions')}
                </span>
            </div>

            {absentReason && currentStatusConfig?.requiresNote && (
                <div className="mt-2 p-2 background border border-color-50 rounded-lg tracking-wider">
                    <span className="text-xs text-gray-600 tracking-wider">
                        {`${t('reason')}: `}
                    </span>
                    <span className="font-semibold text-sm text-gray-800 tracking-wide">
                        {absentReason}
                    </span>
                </div>
            )}

            {/*{status !== null && currentStatusConfig?.requiresNote && (*/}
            {/*    <div*/}
            {/*        onClick={onEdit} // Force entry/re-entry to the reason stage*/}
            {/*        className="mt-2 pt-2 text-center text-xs font-medium text-primary-500 hover:text-primary tracking-wider cursor-pointer transition active:scale-95">*/}
            {/*        {`${t('edit reason')} (${t(currentStatusConfig.display)})`}*/}
            {/*    </div>*/}
            {/*)}*/}
        </div>
    )
};

/**
 * Component cho phép chọn trạng thái điểm danh.
 */
const AttendanceStatusSelector = ({status, onSelect}) => {
    const t = useTranslate();
    const {isLg} = useContext(LayoutContext)

    return (
        <div className="flex flex-col gap-1">
            <h3 className="mb-1 font-medium text-slate-500 tracking-wider">
                {t('Select Attendance Status')}
            </h3>
            <div className="grid grid-cols-2 gap-3">
                {ATTENDANCE_STATUSES.map((s) => {
                    const isSelected = status === s.status;
                    return (
                        <button
                            key={s.status}
                            onClick={() => onSelect(s.status)}
                            className={classNames(
                                "px-3 py-2 flex items-center gap-2 rounded-xl transition-all duration-300",
                                isSelected
                                    ? `${s?.borderColor} border-2`
                                    : `text-gray-800 bg-slate-50 border border-slate-100 hover:border-slate-300 hover:ring-2 hover:ring-indigo-200`
                            )}>
                            {isLg && (
                                <Avatar
                                    size={30}
                                    icon={s?.icon}
                                    className={classNames(
                                        "flex flex-center rounded-lg",
                                        isSelected ? `${s?.colorButton} text-white` : `${s?.colorBg} ${s?.color}`,
                                    )}
                                />
                            )}
                            <div className="flex flex-col items-start">
                                <span className="font-semibold text-sm">
                                    {t(s.display)}
                                </span>
                                <span className="text-xs tracking-wider opacity-70">
                                    {t(s.description)}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    )
};

/**
 * Component cho phép nhập lý do vắng mặt.
 */
const AttendanceReasonInput = ({absentReason, onReasonChange, tempStatusConfig, onBack}) => {
    const t = useTranslate();

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
                <h3 className="mb-1 font-medium text-slate-500 tracking-wider">
                    {t('Enter reason for {status}', {status: t(tempStatusConfig?.display)})}
                </h3>
                {/*<div*/}
                {/*    onClick={onBack}*/}
                {/*    className="text-center text-xs font-medium text-primary-500 hover:text-primary tracking-wider cursor-pointer">*/}
                {/*    {t('Back to Status Selection')}*/}
                {/*</div>*/}
            </div>
            <Input.TextArea
                value={absentReason}
                onChange={onReasonChange}
                placeholder={
                    `${t("Enter reason for {status}",
                        {status: t(tempStatusConfig?.display)}
                    )} ... (${t("Required for saving",
                        {status: t(tempStatusConfig?.display)}
                    )})`
                }
                rows="4"
                className="w-full p-3 resize-none"
                style={{ borderRadius: '0.75rem' }}
            />
        </div>
    )
};

/**
 * Custom input component for entering attendance status with sessions and reason.
 * Behaves like a controlled input via value and onChange props.
 * value: {status: number | null, absent_reason: string, number_of_sessions: number}
 */
const AttendanceInput = ({defaultValues, onValuesChange}) => {
    const t = useTranslate();
    // Destructure value with new names and defaults
    const {status, absent_reason, number_of_sessions} = defaultValues || {
        status: null,
        absent_reason: '',
        number_of_sessions: 1
    };

    // tempStatus controls the view stage (status selection vs reason input)
    // We store the status in temp state to allow toggling back to status selector
    const [tempStatus, setTempStatus] = useState(status);

    useEffect(() => {
        setTempStatus(status);
    }, [status]);

    const currentStatusConfig = useMemo(() => ATTENDANCE_STATUSES.find(s => s.status === status), [status]);
    const tempStatusConfig = useMemo(() => ATTENDANCE_STATUSES.find(s => s.status === tempStatus), [tempStatus]);

    const handleSessionsChange = useCallback((e) => {
        const num = parseInt(e.target.value, 10);
        // Ensure number is at least 1
        const newSessions = isNaN(num) || num < 1 ? 1 : num;
        onValuesChange({status, absent_reason, number_of_sessions: newSessions});
    }, [status, absent_reason, onValuesChange]);

    const handleStatusSelect = useCallback((selectedStatus) => {
        setTempStatus(selectedStatus);

        const config = ATTENDANCE_STATUSES.find(s => s.status === selectedStatus);

        // Preserve current reason if re-selecting the same status
        const newReason = selectedStatus === status ? absent_reason : '';

        // Immediately update the state
        onValuesChange({
            status: selectedStatus,
            number_of_sessions,
            absent_reason: config.requiresNote ? newReason : ''
        });

    }, [onValuesChange, status, absent_reason, number_of_sessions]);

    const handleReasonChange = useCallback((e) => {
        // Only update the reason field while keeping the currently selected status
        onValuesChange({status: tempStatus, number_of_sessions, absent_reason: e.target.value});
    }, [tempStatus, number_of_sessions, onValuesChange]);

    const handleClear = useCallback(() => {
        onValuesChange(null);
        setTempStatus(null);
    }, [onValuesChange]);

    const handleEditReason = useCallback(() => {
        // Force showing the reason input section
        setTempStatus(status);
    }, [status]);

    const handleBackToStatus = useCallback(() => {
        // Force showing the status selector section
        setTempStatus(null);
    }, []);

    // Logic to determine if the reason input view should be shown
    // We show it only if a status is selected, it requires a note, AND the view state (tempStatus) matches the selected status
    const shouldShowReasonInput = status !== null && currentStatusConfig?.requiresNote && (status === tempStatus);

    const adjustSessions = useCallback((amount) => {
        const newSessions = Math.min(
            10,
            Math.max(1, number_of_sessions + amount)
        )

        onValuesChange({
            status,
            absent_reason,
            number_of_sessions: newSessions,
        })
    }, [
        number_of_sessions,
        status,
        absent_reason,
        onValuesChange,
    ])

    return (
        <div className="w-full flex flex-col gap-4">
            <AttendanceStatusSelector
                status={status}
                onSelect={handleStatusSelect}
            />

            {/* Trạng thái đã chọn */}
            <AttendanceDisplay
                status={status}
                absentReason={absent_reason}
                numberOfSessions={number_of_sessions}
                currentStatusConfig={currentStatusConfig}
                onClear={handleClear}
                onEdit={handleEditReason}
            />

            {/* Số lượng tiết học */}
            <div className="flex flex-col gap-1">
                <h3 className="mb-1 font-medium text-slate-500 tracking-wider">
                    {t('number of sessions of the lesson')}
                </h3>
                <Input
                    type="number" variant="filled"
                    min={1} size='large'
                    value={number_of_sessions}
                    onChange={handleSessionsChange}
                    placeholder="Number of sessions"
                    className="w-full text-center font-bold rounded-xl custom-input-sessions"
                    prefix={
                        <button
                            type="button"
                            onClick={() => adjustSessions(-1)}
                            style={{ width: 40, height: 40 }}
                            className="p-2 flex flex-center background border border-slate-300 hover:border-slate-500 text-slate-600 rounded-lg transition-all duration-300">
                            <PiMinusBold size={18}/>
                        </button>
                    }
                    suffix={
                        <button
                            type="button"
                            onClick={() => adjustSessions(1)}
                            style={{ width: 40, height: 40 }}
                            className="p-2 flex flex-center bg-secondary-50 hover:bg-secondary-100 border border-secondary-400 hover:border-secondary-500 text-secondary-600 rounded-lg transition-all duration-300">
                            <PiPlusBold size={18}/>
                        </button>
                    }
                />
            </div>

            {/* Lý do */}
            {(status === null || !shouldShowReasonInput)
                ? null
                : (
                    <AttendanceReasonInput
                        absentReason={absent_reason}
                        onReasonChange={handleReasonChange}
                        tempStatusConfig={tempStatusConfig}
                        onBack={handleBackToStatus}
                    />
                )
            }
        </div>
    );
};


export default AttendanceInput
